(function () {
  'use strict';

  var ENDPOINT = '/apps/effing-cart-discount';
  var FALLBACK_PREFIX = 'HATSTACK-';
  var timer = null;
  var inFlight = null;
  var lastSignature = '';
  var lastCheckedAt = 0;
  var lastCart = null;
  var checkoutPending = false;
  var subscriptions = { loaded: false, updated: false, checkout: false };

  function delay(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  function allocationToCents(value) {
    // Shopify's /cart.js allocation amounts are already integer cents.
    var cents = Math.round(Number(value || 0));
    return Number.isFinite(cents) && cents > 0 ? cents : 0;
  }

  function isPromotionAllocation(allocation) {
    var application = allocation.discount_application || allocation.discountApplication || {};
    var type = String(application.type || '').toLowerCase();
    if (type === 'automatic') return true;

    // Selleasy/Shopify Functions currently returns an empty application type in
    // /cart.js. Identify only the two configured headwear promotions by title.
    var title = String(application.title || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
    return title === 'buythreegetfourthfree' ||
      title === 'buy3get1free' ||
      title === 'buytwogetone50off' ||
      title === 'buy2get150off';
  }

  function automaticDiscountCents(item) {
    var allocations = item.line_level_discount_allocations || item.discount_allocations || [];
    return allocations.reduce(function (sum, allocation) {
      if (!isPromotionAllocation(allocation)) return sum;
      return sum + allocationToCents(allocation.amount);
    }, 0);
  }

  function findFallbackCode(cart) {
    var candidates = [];
    (cart.discount_codes || []).forEach(function (discount) {
      candidates.push(discount.code || discount.title || '');
    });
    (cart.cart_level_discount_applications || []).forEach(function (application) {
      candidates.push(application.code || application.title || '');
    });
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || item.discount_allocations || []).forEach(function (allocation) {
        var application = allocation.discount_application || allocation.discountApplication || {};
        candidates.push(application.code || application.title || '');
      });
    });
    var match = candidates
      .map(function (value) { return String(value).toUpperCase().split(/\s+/)[0]; })
      .find(function (value) { return value.indexOf(FALLBACK_PREFIX) === 0; });
    return match || undefined;
  }

  function fallbackDiscountCents(cart, code) {
    var wanted = String(code || '').toUpperCase();
    if (!wanted) return 0;
    var lineCents = (cart.items || []).reduce(function (sum, item) {
      return sum + (item.line_level_discount_allocations || item.discount_allocations || [])
        .reduce(function (itemSum, allocation) {
          var application = allocation.discount_application || allocation.discountApplication || {};
          var appliedCode = String(application.code || application.title || '').toUpperCase().split(/\s+/)[0];
          return itemSum + (appliedCode === wanted ? allocationToCents(allocation.amount) : 0);
        }, 0);
    }, 0);
    var cartCents = (cart.cart_level_discount_applications || []).reduce(function (sum, application) {
      var appliedCode = String(application.code || application.title || '').toUpperCase().split(/\s+/)[0];
      return sum + (appliedCode === wanted ? allocationToCents(application.total_allocated_amount) : 0);
    }, 0);
    return Math.max(lineCents, cartCents);
  }

  function promotionSatisfied(cart, result) {
    var expected = Number(result && result.expectedDiscountCents || 0);
    if (!result || !result.promotion || expected < 1) return true;
    var automatic = (cart.items || []).reduce(function (sum, item) {
      return sum + automaticDiscountCents(item);
    }, 0);
    var fallback = fallbackDiscountCents(cart, result.code || findFallbackCode(cart));
    return Math.max(automatic, fallback) >= expected - 1;
  }

  function requiresPreorderCheck(cart) {
    var items = cart.items || [];
    var quantity = items.reduce(function (sum, item) { return sum + Number(item.quantity || 0); }, 0);
    return quantity >= 3 && items.some(function (item) {
      var properties = item.properties || {};
      return Boolean(item.selling_plan_allocation) ||
        String(properties._timesact_preorder || '').toLowerCase() === 'true';
    });
  }

  function cartSignature(cart) {
    return [cart.token || '']
      .concat((cart.items || []).map(function (item) {
        return String(item.variant_id || item.id) + ':' + String(item.quantity);
      }).sort())
      .join('|');
  }

  function cartPayload(cart) {
    return {
      cartToken: String(cart.token || ''),
      appliedFallbackCode: findFallbackCode(cart),
      items: (cart.items || []).map(function (item) {
        return {
          variantId: String(item.variant_id || item.id),
          quantity: Number(item.quantity || 0),
          automaticDiscountCents: automaticDiscountCents(item)
        };
      }).filter(function (item) { return item.quantity > 0; })
    };
  }

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('cart-' + response.status);
      return response.json();
    }).then(function (cart) {
      lastCart = cart;
      return cart;
    });
  }

  function refreshCartUi(cart) {
    if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('eg:headwear-discount-checked', { detail: { cart: cart } }));
  }

  function activeDiscountCodes(cart) {
    return (cart.discount_codes || [])
      .map(function (discount) { return String(discount.code || '').trim(); })
      .filter(Boolean);
  }

  async function removePreviousFallback(cart, replacementCode) {
    var currentCode = findFallbackCode(cart);
    if (!currentCode || currentCode === String(replacementCode || '').toUpperCase()) return cart;

    var retainedCodes = activeDiscountCodes(cart).filter(function (code) {
      return code.toUpperCase().indexOf(FALLBACK_PREFIX) !== 0;
    });
    var response = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: retainedCodes.join(',') })
    });
    if (!response.ok) throw new Error('discount-release-' + response.status);
    return response.json();
  }

  async function applyDiscount(code, cart, expectedDiscountCents) {
    cart = await removePreviousFallback(cart, code);
    var path = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent('/cart');
    var response = await fetch(path, { credentials: 'same-origin', redirect: 'follow' });
    if (!response.ok) throw new Error('discount-link-' + response.status);
    for (var attempt = 0; attempt < 6; attempt += 1) {
      await delay(attempt === 0 ? 350 : 250);
      cart = await fetchCart();
      if (findFallbackCode(cart) === String(code).toUpperCase() &&
          fallbackDiscountCents(cart, code) >= Number(expectedDiscountCents || 0) - 1) {
        refreshCartUi(cart);
        return cart;
      }
    }
    throw new Error('discount-not-confirmed');
  }

  async function checkCart(options) {
    var force = Boolean(options && options.force);
    if (inFlight) return inFlight;
    inFlight = (async function () {
      var cart = await fetchCart();
      if (!cart.items || !cart.items.length || !cart.token) return { action: 'none' };

      var signature = cartSignature(cart);
      if (!force && signature === lastSignature && Date.now() - lastCheckedAt < 30_000) {
        return { action: 'none', reason: 'unchanged' };
      }
      lastSignature = signature;
      lastCheckedAt = Date.now();

      // Total cart quantity is an upper bound on eligible headwear quantity.
      // Treat malformed quantities as unsafe and keep the full server check.
      var totalQuantity = 0;
      var belowPromotionMinimum = (cart.items || []).every(function (item) {
        var quantity = Number(item.quantity);
        if (!Number.isFinite(quantity) || !Number.isInteger(quantity) || quantity < 1) return false;
        totalQuantity += quantity;
        return Number.isSafeInteger(totalQuantity) && totalQuantity < 3;
      }) && (totalQuantity === 1 || totalQuantity === 2);
      if (belowPromotionMinimum && !findFallbackCode(cart)) {
        var unqualified = {
          action: 'none',
          reason: 'client-below-minimum',
          expectedDiscountCents: 0,
          minimumQuantity: null,
          promotion: null
        };
        window.__egHeadwearDiscountGuard = {
          checkedAt: new Date().toISOString(),
          result: unqualified
        };
        return Object.assign({ checkedSignature: signature }, unqualified);
      }

      var response = await fetch(ENDPOINT, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(cartPayload(cart))
      });
      if (!response.ok) throw new Error('guard-' + response.status);
      var result = await response.json();
      if (!result.ok) throw new Error(result.error || 'guard-unavailable');
      if (result.action === 'apply' && result.code) {
        cart = await applyDiscount(result.code, cart, result.expectedDiscountCents);
        lastSignature = cartSignature(cart);
        lastCheckedAt = Date.now();
      } else if (result.action === 'none' &&
          (result.reason === 'automatic-discount-present' || result.reason === 'not-qualified') &&
          findFallbackCode(cart)) {
        cart = await removePreviousFallback(cart);
        refreshCartUi(cart);
        lastSignature = cartSignature(cart);
        lastCheckedAt = Date.now();
      }
      window.__egHeadwearDiscountGuard = {
        checkedAt: new Date().toISOString(),
        result: result
      };
      return Object.assign({ checkedSignature: signature }, result);
    })().catch(function (error) {
      window.__egHeadwearDiscountGuard = {
        checkedAt: new Date().toISOString(),
        error: String(error && error.message || error)
      };
      console.warn('[Effing Gear] Headwear discount check failed open.', error);
      return { action: 'none', reason: 'check-failed' };
    }).finally(function () {
      inFlight = null;
    });
    return inFlight;
  }

  function scheduleCheck(delayMs) {
    window.clearTimeout(timer);
    timer = window.setTimeout(function () { checkCart(); }, delayMs == null ? 1200 : delayMs);
  }

  async function verifyCheckoutReady() {
    for (var attempt = 0; attempt < 3; attempt += 1) {
      try {
        if (inFlight) await inFlight;
        var result = await checkCart({ force: true });
        var cart = await fetchCart();
        if (!cart.items || !cart.items.length) return false;
        if (result.reason === 'check-failed') return !requiresPreorderCheck(cart);
        if (cartSignature(cart) !== result.checkedSignature) continue;
        // cartSignature intentionally excludes discounts. If stale fallback state
        // appeared after the client shortcut, retry so the server can clear it.
        if (result.reason === 'client-below-minimum' && findFallbackCode(cart)) continue;
        if (promotionSatisfied(cart, result)) return true;
      } catch (error) {
        console.warn('[Effing Gear] Checkout discount verification failed.', error);
        return !requiresPreorderCheck(lastCart || {});
      }
    }
    return false;
  }

  function showCheckoutError() {
    var popup = document.getElementById('UpcartPopup');
    var host = popup || document.querySelector('main') || document.body;
    var message = document.getElementById('eg-hatstack-checkout-error');
    if (!message) {
      message = document.createElement('p');
      message.id = 'eg-hatstack-checkout-error';
      message.setAttribute('role', 'alert');
      host.appendChild(message);
    }
    message.textContent = 'We could not confirm your hat savings. Please retry checkout.';
  }

  function proceedToCheckout(event) {
    if (event) {
      if (typeof event.preventDefault === 'function') event.preventDefault();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      else if (typeof event.stopPropagation === 'function') event.stopPropagation();
    }
    if (checkoutPending) return;
    checkoutPending = true;

    // A cart update may still be checking the previous signature. Always run a
    // fresh check after it settles before leaving for checkout.
    verifyCheckoutReady()
      .then(function (ready) {
        if (ready) window.location.assign('/checkout');
        else {
          checkoutPending = false;
          showCheckoutError();
        }
      })
      .catch(function (error) {
        console.warn('[Effing Gear] Checkout discount verification failed.', error);
        checkoutPending = false;
        showCheckoutError();
      });
  }

  function subscribeWhenReady(attempt) {
    if (!subscriptions.loaded && typeof window.upcartSubscribeCartLoaded === 'function') {
      window.upcartSubscribeCartLoaded(function () { scheduleCheck(800); });
      subscriptions.loaded = true;
    }
    if (!subscriptions.updated && typeof window.upcartSubscribeCartUpdated === 'function') {
      window.upcartSubscribeCartUpdated(function () { scheduleCheck(1200); });
      subscriptions.updated = true;
    }
    if (!subscriptions.checkout && typeof window.upcartSubscribeCheckoutClicked === 'function') {
      window.upcartSubscribeCheckoutClicked(function (payload) {
        if (!payload || !payload.event) return;
        proceedToCheckout(payload.event);
      });
      subscriptions.checkout = true;
    }
    if (!subscriptions.checkout && attempt < 40) {
      window.setTimeout(function () { subscribeWhenReady(attempt + 1); }, 250);
    }
  }

  // Capture the UpCart checkout link even when its public checkout callback is
  // late or unavailable. This prevents a fast click from outrunning the guard.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var link = target.closest('#UpcartPopup a[href]');
    if (link) {
      var url = new URL(link.href, window.location.href);
      if (url.origin === window.location.origin &&
          (/^\/checkout(?:\/|$)/.test(url.pathname) || /^\/cart\/c\//.test(url.pathname))) {
        proceedToCheckout(event);
      }
      return;
    }
    if (target.closest('form[action="/cart"] button[name="checkout"]')) proceedToCheckout(event);
  }, true);

  [
    'aftersell-upcart:public-events:cart-loaded',
    'aftersell-upcart:public-events:cart-updated',
    'cart:item-added',
    'cart:updated',
    'cart:changed'
  ].forEach(function (eventName) {
    document.addEventListener(eventName, function () { scheduleCheck(1200); });
  });

  window.addEventListener('pageshow', function () { scheduleCheck(400); }, { once: true });
  subscribeWhenReady(0);
  window.egCheckHeadwearDiscount = function () { return checkCart({ force: true }); };
})();
